import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../authentication/AuthContext";
import {
  SideDrawer, buildMockGameStatus, decisionIdsOf, isValidSlug, SLUG_HINT,
  returnTarget, savePageWithGuards, saveDecisionWithGuards,
} from "./BuilderUtils";
import DecisionEditor from "./DecisionEditor";
import ConsequenceMap from "./ConsequenceMap";
import PageCanvas from "./PageCanvas";
import ScoreMatrix from "./ScoreMatrix";
import ComposerStoryboard, { readOpenPreference } from "./ComposerStoryboard";
import StepCanvas from "./StepCanvas";
import StepTypePicker from "./StepTypePicker";
import { defaultStepForKind, newStepId, KINDS_WITHOUT_CONTENT, stepKind } from "./stepKinds";
import { displayInfoOf, emptyByType, hasDecisionList, stepSectionOf, tabsForType } from "./pageKinds";
import { authoredDecisionIds, fetchPoolPreview, isPoolPage } from "./measurePool";
import { territoryOfPage } from "./territoryConfig";
import { pruneLayerSettings } from "../terrain3d/layerSpecs";
import { TERRITORIES_URL } from "../terrain3d/terrainBase";

import IdentityInspector from "./inspectors/IdentityInspector";
import BackgroundInspector from "./inspectors/BackgroundInspector";
import ContextContentInspector from "./inspectors/ContextContentInspector";
import CustomContentInspector from "./inspectors/CustomContentInspector";
import DecisionsInspector from "./inspectors/DecisionsInspector";
import ScoresInspector from "./inspectors/ScoresInspector";
import GraphsInspector, { DoughnutScoresInspector } from "./inspectors/GraphsInspector";
import TerritoryInspector from "./inspectors/TerritoryInspector";
import StepInspector from "./inspectors/StepInspector";

import "../../builder.css";

/**
 * COMPOSEUR DE PAGE — on édite une page en la regardant.
 *
 * CE QU'IL REMPLACE. Il y avait deux écrans pour le même objet : un formulaire
 * en colonne, où l'on réglait à l'aveugle une page qu'on ne voyait pas, et un
 * aperçu où quelques réglages seulement étaient possibles, dans des modales
 * posées PAR-DESSUS ce qu'elles modifiaient. Trois blocs de configuration
 * étaient écrits deux fois et avaient divergé, et l'aperçu enregistrait à
 * chaque frappe sans jamais dire quand le serveur refusait.
 *
 * LA FORME EST CELLE DE L'ÉDITEUR D'ÉTAPE : une barre de sections numérotées,
 * un inspecteur latéral qui ne recouvre jamais rien, et le rendu réel au
 * centre. Ce vocabulaire existait déjà dans le builder pour les modules 3D ; il
 * n'y avait aucune raison qu'éditer une page se fasse autrement.
 *
 * IL A FINI PAR ABSORBER L'ÉDITEUR D'ÉTAPE LUI-MÊME. Le module territoire
 * s'éditait dans une page plein écran montée À LA PLACE du composeur : ouvrir
 * une étape faisait disparaître l'atelier, la pile, et surtout le bouton
 * « Enregistrer » — qui appartient à la page. On composait un module sans
 * qu'aucune sauvegarde ne soit visible, et il fallait ressortir pour la
 * retrouver. Avant lui il y avait un écran de plus : un aperçu qu'on ne pouvait
 * pas modifier, doublé d'une liste dont le seul geste utile était « ouvrir
 * l'étape ».
 *
 * Un module est donc maintenant un type de page comme les autres, avec ses
 * sections dans la même barre — celles de l'ÉTAPE ouverte au rang principal,
 * celles de la page à droite (cf. `tabsForType`). On arrive directement dans
 * l'éditeur.
 *
 * UNE SEULE CARTE, MONTÉE UNE FOIS. C'est la contrainte qui décide du reste :
 * remonter `TerrainViewer` retélécharge le datapack du territoire — rivers.json
 * fait 4,8 Mo. Pour ce type de page, le centre est donc TOUJOURS `StepCanvas`,
 * et changer d'onglet ne touche jamais qu'à l'inspecteur de gauche.
 *
 * UN BROUILLON, UNE SAUVEGARDE. Tout se modifie librement, une seule écriture à
 * la fin — parce que `/save_page` peut refuser ou demander confirmation
 * (page partagée entre ateliers, parcours cassé), et qu'on ne pose pas ces
 * questions à chaque touche du clavier.
 *
 * IL COMPOSE TOUJOURS DANS UN ATELIER quand c'est possible : les scores d'une
 * page n'existent que dans un `score_dic`, et l'ancien formulaire ouvert depuis
 * la bibliothèque affichait des listes de scores vides sans jamais dire
 * pourquoi. À défaut d'atelier, on le dit.
 */
export default function PageComposer({
  pageId: pageIdProp, newType, config: configProp, onClose, onSaved, onNavigate, onRemoveFromPile,
  // Libellé du bouton de sortie en embarqué. « Fermer » ne dit pas où l'on
  // retombe ; un composeur ouvert par-dessus un autre le dit.
  closeLabel,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const isEmbedded = pageIdProp !== undefined;
  const routePageId = params.pageId;
  const pageId = isEmbedded ? pageIdProp : routePageId;

  // Une page tout juste créée n'est plus « nouvelle » : les sauvegardes
  // suivantes ne doivent plus vérifier l'unicité de son identifiant.
  const [createdId, setCreatedId] = useState(null);
  const isNew = pageId === "new" && !createdId;

  // TYPE DEMANDÉ À LA CRÉATION. Il est choisi avant d'entrer (cf.
  // PageTypePicker) : en embarqué l'écran hôte le passe en propriété, en
  // autonome il voyage dans l'état de la route — le composeur ne s'ouvre plus
  // sur une page de contexte quand on venait en demander une autre. Type
  // inconnu ou absent : contexte, le cas le plus courant.
  const requestedType = (isEmbedded ? newType : location.state?.newType) || "context";
  const newPageType = emptyByType[requestedType] ? requestedType : "context";

  // Territoire de l'atelier hôte, tenu à jour hors du cycle des effets.
  const configTerritoryRef = useRef(null);
  configTerritoryRef.current = configProp?.territory?.id || null;

  const [page, setPage] = useState(emptyByType.context);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(pageId !== "new");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const [allDecisions, setAllDecisions] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [allConfigs, setAllConfigs] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [themes, setThemes] = useState([]);
  const [usedByConfigs, setUsedByConfigs] = useState(null);

  const [tab, setTab] = useState("content");
  // La vue de l'onglet Scores : le réglage de l'affichage, ou la synthèse des
  // impacts qui prend la place de l'aperçu.
  const [scoresView, setScoresView] = useState("display");

  // MESURES MODIFIÉES DEPUIS LES TABLEAUX DE SYNTHÈSE, en attente d'écriture.
  // Une décision est un document à part, partagé entre les pages qui la
  // proposent : on ne l'écrit pas à chaque frappe dans une cellule. Le brouillon
  // de la page et celui des mesures partent donc ensemble, au même « Enregistrer ».
  const [decisionDrafts, setDecisionDrafts] = useState({});
  // Les repères d'édition posés sur le rendu (contours, crayons, « + »).
  const [showChrome, setShowChrome] = useState(true);
  const [decisionPickerOpen, setDecisionPickerOpen] = useState(false);
  const [drawerDecisionId, setDrawerDecisionId] = useState(null);
  const [showStepTypePicker, setShowStepTypePicker] = useState(false);
  const [embeddedStepId, setEmbeddedStepId] = useState(null);
  const [viewCatalog, setViewCatalog] = useState({});
  // Pose d'un point d'intérêt : l'inspecteur arme le mode, la carte le consomme.
  const [placingPoint, setPlacingPoint] = useState(false);
  const viewerRef = useRef(null);
  // Atelier de composition choisi à la main, quand plusieurs emploient la page.
  const [chosenConfigId, setChosenConfigId] = useState(params.configId || null);
  // Laquelle des deux lignes du bandeau du bas est dépliée : "none" | "pile" | "steps".
  const [storyboardOpen, setStoryboardOpen] = useState(readOpenPreference);
  // Page vers laquelle on veut aller alors que le brouillon n'est pas
  // enregistré : la question reste ouverte tant qu'on n'y a pas répondu.
  const [pendingPageId, setPendingPageId] = useState(null);
  // Un tableau de synthèse a vite plus de colonnes et de lignes que la zone
  // centrale n'en peut montrer : il prend alors tout l'écran, inspecteur et
  // déroulé compris.
  const [tableFullscreen, setTableFullscreen] = useState(false);
  // UNE PAGE CONSÉQUENCE OUVERTE PAR-DESSUS CELLE-CI. Corriger la page qu'une
  // règle ajoute au parcours obligeait à quitter le composeur, retrouver la
  // page dans la bibliothèque, puis revenir. Un second composeur se pose donc
  // sur le premier, qui reste monté avec son brouillon : rien à enregistrer
  // avant, et « Retour » rend exactement l'onglet qu'on a laissé.
  // { id: pageId | "new", onCreated?: (id) => void } — `onCreated` est appelé
  // une fois, à la première écriture d'une page neuve, par celui qui l'attend
  // comme cible.
  const [overlayPage, setOverlayPage] = useState(null);

  const apiGet = useCallback(async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      headers: { Authorization: "Bearer " + token },
    });
    return res.json();
  }, [token]);

  const apiPost = useCallback(async (path, body) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res.json();
  }, [token]);

  /* ── Chargements ───────────────────────────────────────────────── */

  // Relu aussi quand un composeur posé par-dessus a écrit : une page renommée
  // ou créée là doit apparaître sous son nouveau titre dans les étiquettes.
  const reloadPages = useCallback(
    () => apiGet("/get_all_pages").then((p) => setAllPages(p || [])),
    [apiGet]
  );

  useEffect(() => {
    apiGet("/get_all_game_configs").then((all) => setAllConfigs(all || [])).catch(() => setAllConfigs([]));
    // Artefact statique produit par le pipeline (api/experiments/terrain3d/build_manifest.py),
    // servi depuis l'Object Storage (cf. terrain3d/terrainBase.js) — pas d'appel
    // à l'API Flask, pas d'auth.
    fetch(TERRITORIES_URL)
      .then((r) => (r.ok ? r.json() : { territories: [] }))
      .then((data) => {
        setTerritories(data.territories || []);
        setThemes(data.themes || []);
      })
      .catch(() => setTerritories([]));
  }, [apiGet]);

  useEffect(() => {
    // Le composeur ne se remonte pas quand on saute d'une page à l'autre depuis
    // le déroulé : c'est ici qu'on remet à zéro ce qui appartenait à la page
    // qu'on quitte, sans quoi une page tout juste créée resterait « nouvelle »
    // et un brouillon abandonné laisserait le bouton Enregistrer allumé.
    setCreatedId(null);
    setDirty(false);
    setJustSaved(false);
    setEmbeddedStepId(null);
    setDecisionDrafts({});

    apiGet("/get_all_decisions").then((d) => setAllDecisions(d || []));
    reloadPages();
    if (pageId === "new") {
      setUsedByConfigs([]);
      const blank = emptyByType[newPageType];
      // Un module créé DEPUIS un atelier hérite de sa carte. La page n'est
      // encore dans aucune pile, donc rien ne peut la déduire (cf.
      // territoryOfPage) : sans ça on demanderait à l'auteur de rechoisir le
      // territoire que l'atelier vient de lui imposer, avec le droit de se
      // tromper. Lu dans une ref pour que l'effet ne se rejoue pas — et
      // n'efface pas le brouillon — à chaque retouche de l'atelier hôte.
      const inherited = configTerritoryRef.current;
      setPage(newPageType === "territory_intro" && inherited
        ? { ...blank, content: { ...blank.content, territory_id: inherited } }
        : blank);
      // Le type vient d'être choisi ; ce qu'il reste à donner pour que la page
      // puisse s'enregistrer, c'est son identifiant et son titre. On ouvre donc
      // sur la Fiche plutôt que sur un contenu qu'aucun « Enregistrer » ne
      // pourra écrire tant que ces deux champs sont vides.
      setTab("identity");
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet(`/get_page/${pageId}`)
      .then((data) => setPage({ ...(emptyByType[data.type] || emptyByType.context), ...data }))
      .finally(() => setLoading(false));
    apiGet(`/get_configs_using_page/${pageId}`).then((r) => setUsedByConfigs(r.used_by || []));
  }, [pageId, newPageType, apiGet, reloadPages]);

  /* ── Contexte d'atelier ────────────────────────────────────────── */

  // Ateliers dont la pile contient cette page : ce sont eux qui peuvent servir
  // de décor de composition (leur score_dic, leur territoire).
  const owners = useMemo(
    () => allConfigs.filter((c) => (c.pile || []).includes(page._id)),
    [allConfigs, page._id]
  );

  const config = useMemo(() => {
    if (configProp) return configProp;
    const chosen = allConfigs.find((c) => c._id === chosenConfigId);
    if (chosen) return chosen;
    return owners.length === 1 ? owners[0] : null;
  }, [configProp, allConfigs, chosenConfigId, owners]);

  const scoreDic = config?.score_dic || null;

  // Le déroulé de l'atelier. Une pile appartient à un atelier, pas à une page :
  // sans contexte d'atelier, il n'y a rien à dérouler.
  const pile = config?.pile || [];

  const pagesById = useMemo(() => {
    const map = {};
    allPages.forEach((p) => { map[p._id] = p; });
    // La page ouverte est montrée telle qu'on est en train de l'écrire, pas
    // telle qu'elle est en base : la renommer doit se voir aussitôt dans le
    // déroulé, sans quoi on croirait la modification perdue.
    if (page._id) map[page._id] = page;
    return map;
  }, [allPages, page]);

  const pageTerritory = useMemo(
    () => territoryOfPage(page._id, allConfigs),
    [page._id, allConfigs]
  );

  /* ── Brouillon ─────────────────────────────────────────────────── */

  const patch = useCallback((fields) => {
    setPage((p) => ({ ...p, ...fields }));
    setDirty(true);
    setJustSaved(false);
  }, []);

  const patchContent = useCallback((fields) => {
    setPage((p) => ({ ...p, content: { ...p.content, ...fields } }));
    setDirty(true);
    setJustSaved(false);
  }, []);

  const changeType = (newType) => {
    setPage((p) => ({
      ...emptyByType[newType], _id: p._id, title: p.title, description: p.description, tags: p.tags,
    }));
    setDirty(true);
  };

  // Le module d'intro porte son propre `territory_id`. On le maintient d'accord
  // avec l'atelier plutôt que de le laisser diverger : deux endroits nommant la
  // même chose, c'est une reconstruction de scène en pleine séance le jour où
  // ils ne disent plus pareil.
  useEffect(() => {
    if (page.type !== "territory_intro") return;
    if (pageTerritory.free || pageTerritory.conflicting) return;
    if (page.content?.territory_id === pageTerritory.territoryId) return;
    setPage((p) => ({ ...p, content: { ...p.content, territory_id: pageTerritory.territoryId } }));
  }, [page.type, page.content?.territory_id, pageTerritory]);

  /* ── Onglets ───────────────────────────────────────────────────── */

  const tabs = useMemo(() => tabsForType(page.type), [page.type]);

  // Changer de type peut retirer l'onglet ouvert (une page de score n'a pas de
  // suites) : on retombe sur le premier plutôt que sur un panneau vide.
  useEffect(() => {
    if (!tabs.some((t) => t.id === tab)) setTab(tabs[0]?.id || "identity");
  }, [tabs, tab]);

  /* ── Le tirage d'exemple d'une page probabiliste ────────────────── */

  // Graine de l'aperçu : la changer, c'est « autre tirage ». Elle ne quitte pas
  // le composeur — en jeu, chaque partie a la sienne.
  const [previewSeed, setPreviewSeed] = useState("apercu");
  const [poolPreview, setPoolPreview] = useState(null);

  /* ── La page telle qu'elle se joue ─────────────────────────────── */

  const decisionsById = useMemo(() => {
    const map = {};
    allDecisions.forEach((d) => { map[d._id] = d; });
    // Ce qu'on est en train d'écrire prime sur ce qui est en base : le rendu de
    // la page comme les tableaux montrent la modification avant qu'elle parte.
    Object.entries(decisionDrafts).forEach(([id, doc]) => { map[id] = doc; });
    return map;
  }, [allDecisions, decisionDrafts]);

  const changeDecision = useCallback((decId, next) => {
    setDecisionDrafts((drafts) => ({ ...drafts, [decId]: next }));
    setJustSaved(false);
  }, []);

  const draftedDecisionIds = Object.keys(decisionDrafts);
  // « Modifié » vaut pour l'ensemble de ce qui partira : la page ET les mesures
  // retouchées depuis les tableaux.
  const anyDirty = dirty || draftedDecisionIds.length > 0;

  // Le canevas a besoin des décisions RÉSOLUES (une carte affiche une image, un
  // titre, des impacts) alors que le brouillon ne garde que des identifiants —
  // c'est lui qu'on enregistre. La résolution se fait donc ici, à l'affichage,
  // et le rendu suit la moindre modification sans aucun aller-retour serveur.
  const playedPage = useMemo(() => {
    if (!hasDecisionList(page.type)) return page;
    // UNE PAGE QUI PIOCHE N'A RIEN À MONTRER TANT QUE PERSONNE N'A TIRÉ. Le
    // canevas affiche donc un tirage d'exemple, calculé par le serveur avec la
    // même fonction que le jeu (cf. /pool_preview) — un aperçu qui aurait sa
    // propre logique finirait par montrer autre chose que ce qui sera joué.
    const shownIds = isPoolPage(page) ? (poolPreview?.draws?.[0] || []) : decisionIdsOf(page);
    return {
      ...page,
      content: {
        ...page.content,
        list_decisions: shownIds.map((id) => decisionsById[id]).filter(Boolean),
      },
    };
  }, [page, decisionsById, poolPreview]);

  // Les scores que la page remet à une valeur de départ (`content.score_resets`)
  // sont montrés à cette valeur : c'est ce que le serveur fait en entrée de
  // page, et c'est le compteur que l'auteur vient régler.
  const scoreResets = page.content?.score_resets;
  const gameStatus = useMemo(() => {
    const status = buildMockGameStatus(scoreDic, config?.decision_metric_id, config);
    if (!Array.isArray(scoreResets) || !scoreResets.length) return status;
    const scoreDicWithResets = { ...status.score_dic };
    scoreResets.forEach(({ score_id, value }) => {
      if (scoreDicWithResets[score_id] && typeof value === "number") {
        scoreDicWithResets[score_id] = { ...scoreDicWithResets[score_id], value };
      }
    });
    return { ...status, score_dic: scoreDicWithResets };
  }, [scoreDic, config, scoreResets]);

  // DIX TIRAGES, PAS UN. Le canevas n'en montre qu'un, mais l'inspecteur a
  // besoin des autres pour dire ce que le réglage produit RÉGULIÈREMENT — un
  // aperçu unique se lit comme une maquette et laisse croire que la page est
  // celle-là. La signature limite les allers-retours à ce qui change vraiment
  // le tirage : le réservoir, les bandes, le réglage de la page, la graine.
  const drawSignature = JSON.stringify([
    isPoolPage(page) ? page.content?.selection : null,
    config?.measure_pool,
    config?.decision_metric_id,
    previewSeed,
  ]);
  useEffect(() => {
    if (!isPoolPage(page) || !config) return setPoolPreview(null);
    let cancelled = false;
    fetchPoolPreview(token, {
      config,
      page,
      decisions: Object.values(decisionDrafts),
      seed: previewSeed,
      samples: 10,
    })
      .then((data) => { if (!cancelled) setPoolPreview(data); })
      .catch(() => { if (!cancelled) setPoolPreview(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawSignature, token]);

  /**
   * QUAND FAUT-IL REMONTER LE CANEVAS.
   *
   * Les composants de jeu ne sont pas écrits pour être re-configurés en vol :
   * MosaicPage fige par exemple la liste des scores de son bandeau à la
   * construction (`useState(() => …)`). Sans remontage, ajouter un score se
   * verrait sur les cartes mais pas en haut de l'écran — un aperçu à moitié
   * vrai est pire qu'un aperçu figé.
   *
   * On remonte donc quand cette configuration-là change, et à ce moment
   * seulement. Le module de territoire n'est pas concerné : il ne passe pas par
   * ce canevas, il a sa propre carte montée une fois pour toutes.
   */
  const canvasKey = useMemo(
    () => `${page.type}|${JSON.stringify(page.content?.display_score_decisions || [])}`
      + `|${JSON.stringify(scoreResets || [])}`,
    [page.type, page.content?.display_score_decisions, scoreResets]
  );

  /* ── Étapes du module « territoire 3D » ────────────────────────── */

  const steps = page.content?.steps || [];
  const basePath = params.configId
    ? `/builder/config/${params.configId}/page/${pageId}`
    : `/builder/page/${pageId}`;

  // En autonome l'étape ouverte est dans l'URL (lien partageable, retour
  // navigateur naturel). En embarqué — le composeur est monté par-dessus
  // l'éditeur d'atelier — naviguer ferait quitter l'atelier : elle reste locale.
  const activeStepId = isEmbedded ? embeddedStepId : params.stepId;
  const namedStepIndex = activeStepId ? steps.findIndex((s) => s.step_id === activeStepId) : -1;

  // IL Y A TOUJOURS UNE ÉTAPE OUVERTE, et c'est la première par défaut. Arriver
  // sur un module, c'est arriver dans son éditeur — pas devant un sommaire.
  // Une URL qui désigne une étape supprimée retombe donc là aussi, plutôt que
  // sur un écran d'erreur pour une donnée qui n'a plus d'importance.
  const activeStepIndex = namedStepIndex >= 0 ? namedStepIndex : 0;
  const activeStep = steps[activeStepIndex] || null;

  const openStep = (idx) => {
    const target = steps[idx];
    if (!target) return;
    if (isEmbedded) return setEmbeddedStepId(target.step_id);
    navigate(`${basePath}/step/${target.step_id}`, {
      replace: !!params.stepId, // naviguer dans le storyboard n'empile pas l'historique
      state: location.state,
    });
  };

  const updateStep = (idx, next) => patchContent({ steps: steps.map((s, i) => (i === idx ? next : s)) });

  // Poser un point d'intérêt : la carte rend la position cliquée, l'étape la
  // range. Ce va-et-vient vit ici parce que l'inspecteur (qui arme le mode) et
  // le canevas (qui reçoit le clic) sont deux frères, pas un parent et un fils.
  const addPoiFromClick = (position) => {
    if (!activeStep) return;
    const points = [...(activeStep.points_of_interest?.points || [])];
    points.push({ point_id: newStepId(), position, title: "", image_url: "", text: "" });
    updateStep(activeStepIndex, {
      ...activeStep,
      points_of_interest: { ...(activeStep.points_of_interest || {}), points },
    });
    setPlacingPoint(false);
  };

  const createStep = (kind) => {
    // La nouvelle étape reprend le chapitre de la dernière : on écrit un module
    // sujet par sujet, et retaper le même nom à chaque écran serait le meilleur
    // moyen de ne plus le taper du tout.
    const previous = steps[steps.length - 1];
    const draft = { ...defaultStepForKind(kind), chapter: previous?.chapter || "" };
    patchContent({ steps: [...steps, draft] });
    setShowStepTypePicker(false);
    // La nouvelle étape s'ouvre, et sur sa section Type : c'est là qu'on vient
    // de choisir sa forme, la suite se règle dans l'ordre des onglets.
    setTab("step:type");
    if (isEmbedded) setEmbeddedStepId(draft.step_id);
    else navigate(`${basePath}/step/${draft.step_id}`, { state: location.state });
  };

  const removeStep = (idx) => {
    const list = steps.filter((unused, i) => i !== idx);
    patchContent({ steps: list });
    // Supprimer l'étape ouverte laisserait l'éditeur pointer dans le vide : on
    // bascule sur la voisine. Sans voisine, on retire simplement la mention de
    // l'URL — le module n'a plus d'étape, et le canevas le dit lui-même.
    if (steps[idx]?.step_id === activeStepId) {
      const fallback = list[Math.min(idx, list.length - 1)];
      if (isEmbedded) setEmbeddedStepId(fallback?.step_id || null);
      else {
        navigate(fallback ? `${basePath}/step/${fallback.step_id}` : basePath, {
          replace: true, state: location.state,
        });
      }
    }
  };

  // UN MODULE SANS ÉTAPE N'A PAS DE SECTION D'ÉTAPE À OUVRIR. On bascule alors
  // sur « Module », d'où l'on choisit le territoire et d'où part la première
  // étape — plutôt que de laisser l'onglet Type ouvert sur un panneau vide.
  useEffect(() => {
    if (page.type !== "territory_intro") return;
    if (steps.length > 0) return;
    setTab((current) => (current.startsWith("step:") ? "module" : current));
  }, [page.type, steps.length]);

  /* ── Décisions ─────────────────────────────────────────────────── */

  const openDecisionEditor = (decId) => setDrawerDecisionId(decId || "new");

  const closeDecisionDrawer = async () => {
    const wasNew = drawerDecisionId === "new";
    setDrawerDecisionId(null);
    const list = await apiGet("/get_all_decisions");
    if (wasNew) {
      // DecisionEditor embarqué ne renvoie pas l'id créé : on le déduit en
      // comparant le catalogue avant/après, et on rattache la nouvelle décision.
      const previous = new Set(allDecisions.map((d) => d._id));
      const created = (list || []).find((d) => !previous.has(d._id));
      if (created) patchContent({ list_decisions: [...decisionIdsOf(page), created._id] });
    }
    setAllDecisions(list || []);
  };

  // Duplique une décision de CETTE page : crée une copie indépendante et
  // rebranche cette entrée dessus, pour que les autres pages gardent l'originale.
  const duplicateDecisionInList = async (idx, decId) => {
    const suggested = `${decId}_copie`;
    const newId = window.prompt(
      `Identifiant de la copie de la décision « ${decId} » :`,
      allDecisions.some((d) => d._id === suggested) ? "" : suggested
    );
    if (!newId) return;
    if (!isValidSlug(newId)) return alert(`Identifiant invalide.\n${SLUG_HINT}`);
    if (allDecisions.some((d) => d._id === newId)) {
      return alert(`L'identifiant « ${newId} » est déjà utilisé par une autre décision.`);
    }
    const res = await apiPost("/duplicate_decision", { source_id: decId, new_id: newId });
    if (!res.success) return alert("Erreur : " + res.message);
    patchContent({ list_decisions: decisionIdsOf(page).map((id, i) => (i === idx ? newId : id)) });
    setAllDecisions((prev) => [...prev, res.decision]);
  };

  /* ── Enregistrer, supprimer, sortir ────────────────────────────── */

  const backTo = returnTarget(location);

  const handleSave = async () => {
    const payload = { ...page };
    if (payload.round === "") delete payload.round;
    // REVENIR À UNE LISTE FIGÉE, C'EST NE PLUS AVOIR DE RÉGLAGE DE TIRAGE — pas
    // en avoir un vide. La bascule pose `selection: null` dans le brouillon
    // (pour que le rendu suive tout de suite) ; on retire la clé avant
    // d'écrire, sinon la page porterait une trace de tirage qu'elle n'utilise
    // pas, et que les 200 pages existantes n'ont pas.
    if (payload.content && payload.content.selection == null) {
      payload.content = { ...payload.content };
      delete payload.content.selection;
    }
    // Les vues n'enregistrent que ce que l'auteur a réellement décidé : tout
    // réglage encore égal au défaut du registre est retiré ici. Sinon chaque
    // étape figerait les défauts du jour de sa création, et les faire évoluer
    // ne changerait plus rien aux modules existants.
    if (payload.type === "territory_intro" && payload.content?.steps) {
      payload.content = {
        ...payload.content,
        steps: payload.content.steps.map((step) => ({
          ...step,
          layer_settings: pruneLayerSettings(step.layer_settings, viewCatalog),
        })),
      };
    }

    setSaving(true);

    // LES MESURES D'ABORD. Elles sont modifiables depuis les tableaux de
    // synthèse et sont des documents partagés : on prévient une fois, en les
    // nommant, plutôt qu'à chaque décision — l'auteur vient d'en régler cinq
    // d'affilée, cinq questions identiques ne se lisent plus.
    if (draftedDecisionIds.length > 0) {
      const names = draftedDecisionIds
        .map((id) => decisionsById[id]?.title || id)
        .map((name) => `  • ${name}`)
        .join("\n");
      const proceed = window.confirm(
        `${draftedDecisionIds.length} mesure(s) ont été modifiées :\n${names}\n\n` +
        `Elles sont partagées avec toutes les pages qui les proposent : la ` +
        `modification s'y appliquera aussi. Continuer ?`
      );
      if (!proceed) { setSaving(false); return false; }

      const saved = [];
      for (const decId of draftedDecisionIds) {
        const result = await saveDecisionWithGuards(decisionDrafts[decId], { token, confirmUsage: false });
        if (!result.ok) {
          setSaving(false);
          // On s'arrête à la première refusée en gardant les brouillons : la
          // page n'est pas écrite non plus, donc rien n'est à moitié fait.
          if (!result.cancelled) {
            alert(`La mesure « ${decisionsById[decId]?.title || decId} » n'a pas été enregistrée.\n\n${result.message}`);
          }
          return false;
        }
        saved.push(decId);
      }
      const savedDocs = saved.map((decId) => decisionDrafts[decId]);
      setAllDecisions((prev) => prev.map((d) => savedDocs.find((n) => n._id === d._id) || d));
      setDecisionDrafts({});
    }

    const outcome = await savePageWithGuards(payload, {
      token, isNew, existingIds: allPages.map((p) => p._id), usedByConfigs,
    });
    setSaving(false);

    if (!outcome.ok) {
      if (!outcome.cancelled) alert("Erreur : " + outcome.message);
      return false;
    }

    setDirty(false);
    setJustSaved(true);
    onSaved?.(payload._id);
    if (isNew) {
      setCreatedId(payload._id);
      setAllPages((prev) => [...prev, payload]);
      // L'URL désignait « new » : la remplacer permet de recharger, de partager
      // le lien, et surtout d'éviter qu'un second enregistrement se croie neuf.
      if (!isEmbedded) {
        navigate(
          params.configId ? `/builder/config/${params.configId}/page/${payload._id}` : `/builder/page/${payload._id}`,
          { replace: true, state: location.state }
        );
      }
    }
    return true;
  };

  /* ── Naviguer dans le déroulé ──────────────────────────────────── */

  // Changer de page sans quitter le composeur. En autonome c'est l'URL qui
  // change (lien partageable, retour navigateur) ; en embarqué c'est l'écran
  // hôte qui désigne la page composée, donc on le lui demande.
  const openPage = useCallback((nextId) => {
    setDirty(false);
    setDecisionDrafts({});
    setPendingPageId(null);
    if (isEmbedded) { onNavigate?.(nextId); return; }
    navigate(
      params.configId ? `/builder/config/${params.configId}/page/${nextId}` : `/builder/page/${nextId}`,
      { state: location.state }
    );
  }, [isEmbedded, onNavigate, navigate, params.configId, location.state]);

  // SAUTER D'UNE PAGE À L'AUTRE N'EST PAS LE MÊME GESTE QUE CHANGER D'ÉTAPE.
  // Les étapes d'un module partagent un seul brouillon, on y circule librement.
  // Chaque page, elle, est un document à part : partir sans enregistrer perd le
  // travail. Un `confirm` ne proposerait que « abandonner ou rester » — or ce
  // qu'on veut presque toujours, c'est enregistrer et continuer. D'où une vraie
  // question, à trois réponses.
  const goToPage = (nextId) => {
    if (!nextId || nextId === page._id) return;
    if (anyDirty) return setPendingPageId(nextId);
    openPage(nextId);
  };

  /**
   * RETIRER LA PAGE DE CET ATELIER — le geste courant, et le seul qui soit
   * réversible. Il ne touche ni la page ni les autres ateliers : la pile de
   * l'atelier hôte perd une entrée, c'est tout. C'est presque toujours ce qu'on
   * veut quand on compose un atelier, alors que le bouton d'avant supprimait la
   * page de la base pour tout le monde.
   */
  const handleRemoveFromPile = () => {
    const label = page.title || page._id;
    const proceed = window.confirm(
      `Retirer « ${label} » de la pile de ${config?.name || config?._id || "cet atelier"} ?\n\n` +
      `La page n'est pas supprimée : elle reste dans la bibliothèque et dans les ` +
      `autres ateliers qui l'utilisent.`
    );
    if (!proceed) return;
    if (dirty && !window.confirm("Cette page a des modifications non enregistrées. Les abandonner ?")) return;
    setDirty(false);
    setDecisionDrafts({});
    onRemoveFromPile(page._id);
  };

  const handleDelete = async () => {
    const usedIn = usedByConfigs || [];
    const warning = usedIn.length > 0
      ? `\n\n⚠ Cette page est utilisée dans ${usedIn.length} atelier(s) : ${usedIn.join(", ")}. La supprimer cassera leur parcours.`
      : "";
    if (!window.confirm(`Supprimer la page « ${page._id} » ?${warning}`)) return;
    // `force` : l'API refuse de supprimer une page employée, et la
    // confirmation ci-dessus vient de nommer les ateliers touchés.
    const res = await fetch(`${process.env.REACT_APP_API_URL}/delete_page/${page._id}?force=true`, {
      method: "DELETE", headers: { Authorization: "Bearer " + token },
    }).then((r) => r.json());
    if (!res.success) return alert("Erreur : " + res.message);
    setDirty(false);
    onSaved?.(null);
    if (isEmbedded) onClose?.();
    else navigate(backTo.path);
  };

  const leave = useCallback(() => {
    if (anyDirty && !window.confirm("Cette page a des modifications non enregistrées. Les abandonner ?")) return;
    if (isEmbedded) onClose?.();
    else navigate(backTo.path);
  }, [anyDirty, isEmbedded, onClose, navigate, backTo.path]);

  // Échap ferme la chose la plus récemment ouverte, jamais le composeur tant
  // qu'il reste un panneau par-dessus.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Escape") return;
      // Le composeur posé par-dessus a son propre Échap : celui-ci ne doit pas
      // fermer les deux d'un coup.
      if (drawerDecisionId !== null || showStepTypePicker || overlayPage !== null) return;
      // Une question posée attend une réponse : Échap la referme, il ne quitte
      // pas le composeur par-dessus elle.
      if (pendingPageId) { setPendingPageId(null); return; }
      if (tableFullscreen) { setTableFullscreen(false); return; }
      leave();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [leave, drawerDecisionId, showStepTypePicker, pendingPageId, tableFullscreen, overlayPage]);

  /* ── Une page conséquence, éditée par-dessus ─────────────────────── */

  const openPageOverlay = useCallback((id) => {
    // La page ouverte est déjà là, sous nos yeux : un second brouillon du même
    // document se battrait avec le premier à l'enregistrement.
    if (id === page._id) return;
    setOverlayPage({ id });
  }, [page._id]);

  // Qui attend l'identifiant d'une page neuve. Dans une ref et non dans l'état :
  // c'est un rappel à consommer une fois, pas quelque chose à rendre.
  const overlayCreatedRef = useRef(null);

  const createPageOverlay = useCallback((onCreated) => {
    overlayCreatedRef.current = onCreated;
    setOverlayPage({ id: "new" });
  }, []);

  const closePageOverlay = useCallback(() => {
    overlayCreatedRef.current = null;
    setOverlayPage(null);
    reloadPages();
  }, [reloadPages]);

  const onOverlaySaved = useCallback((savedId) => {
    reloadPages();
    // `null` = la page vient d'être supprimée depuis la surcouche.
    if (!savedId) return;
    const onCreated = overlayCreatedRef.current;
    if (onCreated) {
      overlayCreatedRef.current = null;
      onCreated(savedId);
    }
  }, [reloadPages]);

  // Le composeur couvre l'écran : laisser la page qui l'héberge défiler
  // derrière lui donnerait l'impression que c'est le canevas qui bouge.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Fermer l'onglet du navigateur avec un brouillon en cours doit au moins poser
  // la question — c'est la seule protection que le navigateur nous laisse.
  useEffect(() => {
    if (!anyDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [anyDirty]);

  /* ── Rendu ─────────────────────────────────────────────────────── */

  if (loading) return <div className="builder-loading">Chargement…</div>;

  const typeInfo = displayInfoOf(page);
  const showFullscreen = tableFullscreen;
  const isModule = page.type === "territory_intro";
  const territory = territories.find((t) => t.id === page.content?.territory_id);
  const stepSection = stepSectionOf(tab);

  /* CE QUE LA ZONE CENTRALE MONTRE. Pour un module, toujours la carte : elle
     est montée une fois et ne bouge plus d'un onglet à l'autre (cf. l'en-tête
     de ce fichier). Pour les autres pages, l'aperçu — sauf sur les onglets qui
     appellent un tableau. */
  const mainView = isModule
    ? "territory"
    : tab === "consequences"
      ? "consequences"
      : tab === "scores" && scoresView === "synthesis"
        ? "scores"
        : "canvas";

  const ctx = {
    scoreDic, allDecisions, allPages, territories, pageTerritory, token, isNew, usedByConfigs, page,
    // L'atelier entier, et pas seulement son score_dic : le réservoir de
    // mesures et ses bandes de coût vivent là, et c'est d'eux que dépend le
    // réglage d'une page qui pioche.
    config,
  };
  const inspectorProps = { page, onPatch: patch, onPatchContent: patchContent, ctx };

  // Le bandeau du bas compte une ligne, deux, ou aucune : la hauteur rendue au
  // canevas en dépend, et c'est elle que `--page-chrome` transporte au CSS.
  const storyboardRows = (pile.length > 0 ? 1 : 0) + (isModule ? 1 : 0);

  // LE REPLI EST MÉMORISÉ GLOBALEMENT, MAIS LES LIGNES SONT PROPRES À LA PAGE.
  // Une préférence « étapes dépliées » n'a rien à déplier sur une page
  // ordinaire — et lui réserverait pourtant sa hauteur, laissant une bande
  // vide sous le canevas. On retombe donc sur « rien d'ouvert » ici, sans
  // toucher à la préférence : elle vaudra de nouveau au prochain module.
  const storyboardOpenHere =
    (storyboardOpen === "steps" && !isModule) || (storyboardOpen === "pile" && pile.length === 0)
      ? "none"
      : storyboardOpen;

  return (
    <div className={
      "page-composer"
      + (storyboardRows > 0 ? " has-storyboard" : "")
      + (storyboardRows > 1 ? " has-two-storyboards" : "")
      + (storyboardOpenHere !== "none" ? " has-open-storyboard" : "")
    }>
      <header className="step-editor-bar">
        <button type="button" className="step-editor-back" onClick={leave}>
          ← {isEmbedded ? (closeLabel || "Fermer") : backTo.label}
        </button>

        <span className="step-editor-bar-title">
          <span className="page-composer-id">{typeInfo?.icon} {isNew ? "Nouvelle page" : page._id}</span>
          {/* Pour un module, le titre dit L'ÉTAPE OUVERTE plutôt que le type de
              page : c'est elle qu'on édite, et c'est la seule information de la
              barre qui change quand on circule dans la séquence. */}
          {isModule && activeStep ? (
            <>
              {" · "}Étape {activeStepIndex + 1} sur {steps.length}
              {" · "}{stepKind(activeStep.kind)?.label || activeStep.kind}
            </>
          ) : (
            <>{" · "}{typeInfo?.label || page.type}</>
          )}
        </span>

        {!isEmbedded && owners.length > 0 && (
          <label className="page-composer-context" title="Atelier dont les scores servent à composer et à prévisualiser cette page">
            Dans l'atelier
            <select
              className="builder-input small"
              value={config?._id || ""}
              onChange={(e) => setChosenConfigId(e.target.value)}
            >
              {owners.map((c) => <option key={c._id} value={c._id}>{c.name || c._id}</option>)}
            </select>
          </label>
        )}

        {/* DEUX GROUPES QUAND IL Y A DEUX NIVEAUX D'OBJET. Les sections de
            l'étape ouverte au rang principal, numérotées ; celles de la page
            derrière un séparateur, sans numéro — elles ne font pas partie de la
            séquence de travail. Un module sans étape n'affiche que le second
            groupe : il n'y a rien à régler dans une étape qui n'existe pas. */}
        <nav className="step-editor-tabs" aria-label="Sections de la page">
          {tabs.map((entry, i) => {
            const section = stepSectionOf(entry.id);
            if (section && !activeStep) return null;
            const startsPageGroup = entry.group === "page" && tabs[i - 1]?.group === "step";
            const disabled = section === "content"
              && KINDS_WITHOUT_CONTENT.includes(activeStep?.kind);
            return (
              <React.Fragment key={entry.id}>
                {startsPageGroup && <span className="step-editor-tabs-split" aria-hidden="true" />}
                <button
                  type="button"
                  className={
                    `step-editor-tab${tab === entry.id ? " is-active" : ""}`
                    + (entry.group === "page" && tabs.some((t) => t.group === "step") ? " is-aside" : "")
                  }
                  onClick={() => setTab(entry.id)}
                  aria-current={tab === entry.id}
                  title={entry.hint}
                  disabled={disabled}
                >
                  {entry.index && <span className="step-editor-tab-index">{entry.index}</span>}
                  {entry.label}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="page-composer-actions">
          {/* Les repères d'édition (contours, crayons, case « + ») sont posés
              SUR le rendu : ils sont ce qui permet de cliquer là où l'on
              regarde, mais ils recouvrent aussi ce qu'ils désignent. Ce bouton
              les retire le temps de vérifier ce que les participants verront. */}
          {mainView === "canvas" && (
            <button
              type="button"
              className="builder-btn-secondary"
              onClick={() => setShowChrome((v) => !v)}
              title={showChrome ? "Masquer les repères d'édition pour voir la page telle qu'elle sera jouée" : "Réafficher les repères d'édition"}
            >
              {showChrome ? "👁 Aperçu net" : "✏️ Repères"}
            </button>
          )}
          <span className={`page-composer-state${anyDirty ? " is-dirty" : ""}`}>
            {anyDirty ? "Modifié" : justSaved ? "Enregistré" : ""}
          </span>
          {!isNew && onRemoveFromPile && config && (
            <button
              className="builder-btn-secondary"
              onClick={handleRemoveFromPile}
              title={`Retirer cette page de la pile de ${config.name || config._id}, sans la supprimer`}
            >Retirer de l'atelier</button>
          )}
          <button className="builder-btn-primary" onClick={handleSave} disabled={saving || (!anyDirty && !isNew)}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </header>

      <div className="step-editor-main">
        {/* PAS D'INSPECTEUR SUR L'ONGLET CONSÉQUENCES. Son tableau se lit en
            largeur — une ligne par mesure, deux colonnes — et le panneau de
            gauche n'y aurait porté qu'une redite de ce que les étiquettes
            disent déjà, en lui volant 380 px. */}
        {tab !== "consequences" && (
        <aside className={`step-editor-inspector${tab === "background" ? " is-wide" : ""}`}>
          <Inspector
            tab={tab}
            inspectorProps={inspectorProps}
            config={config}
            owners={owners}
            onChangeType={changeType}
            onDeletePage={isNew ? null : handleDelete}
            scoresView={scoresView}
            onScoresViewChange={setScoresView}
            decisionsById={decisionsById}
            decisionPickerOpen={decisionPickerOpen}
            onDecisionPickerOpenChange={setDecisionPickerOpen}
            onEditDecision={openDecisionEditor}
            onCreateDecision={() => openDecisionEditor("new")}
            onDuplicateDecision={duplicateDecisionInList}
            poolPreview={poolPreview}
            onReroll={() => setPreviewSeed(Math.random().toString(36).slice(2))}
            onOpenStep={openStep}
            onAddStep={() => setShowStepTypePicker(true)}
            onRemoveStep={removeStep}
            onReorderSteps={(next) => patchContent({ steps: next })}
            stepSection={stepSection}
            step={activeStep}
            stepIndex={activeStepIndex}
            steps={steps}
            territory={territory}
            themes={themes}
            viewCatalog={viewCatalog}
            placingPoint={placingPoint}
            onPlacingPointChange={setPlacingPoint}
            onChangeStep={(next) => updateStep(activeStepIndex, next)}
          />
        </aside>
        )}

        {/* LA ZONE CENTRALE N'EST PAS TOUJOURS L'APERÇU. Certaines questions ne
            se règlent pas en regardant une page : équilibrer des impacts, ou
            suivre ce que chaque mesure déclenche, demande de tout voir en même
            temps. Ces onglets-là remplacent donc le rendu par un tableau, plutôt
            que de le tasser dans la colonne de gauche. */}
        <div className={
          "page-composer-canvas"
          + (mainView === "canvas" || mainView === "territory" ? "" : " is-table")
          + (mainView === "territory" ? " is-territory" : "")
          + (mainView !== "canvas" && mainView !== "territory" && showFullscreen ? " is-fullscreen" : "")
        }>
          {/* LA CARTE DU MODULE, MONTÉE UNE SEULE FOIS pour toute la durée de
              l'édition : elle ne dépend ni de l'onglet ouvert, ni de l'étape —
              c'est `StepCanvas` qui fait voler la caméra de l'une à l'autre. La
              remonter coûterait un datapack complet à chaque clic. */}
          {mainView === "territory" && (activeStep ? (
            <StepCanvas
              ref={viewerRef}
              territoryId={page.content?.territory_id}
              availableLayers={page.content?.layers || []}
              fogEnabled={page.content?.fog_enabled !== false}
              step={activeStep}
              previousStep={activeStepIndex > 0 ? steps[activeStepIndex - 1] : null}
              steps={steps}
              stepIndex={activeStepIndex}
              gameStatus={gameStatus}
              onCapturePose={() => {
                const pose = viewerRef.current?.getCurrentPose();
                if (pose) updateStep(activeStepIndex, { ...activeStep, camera: pose });
              }}
              onForgetPose={() => updateStep(activeStepIndex, { ...activeStep, camera: null })}
              placingPoint={placingPoint}
              onTerrainClick={addPoiFromClick}
              // Sur l'onglet Carte, le fantôme se réduit à son contour : sinon un
              // « grand panneau centré » cache la carte qu'on est venu régler.
              ghostMode={stepSection === "map" ? "outline" : "full"}
              onCatalogChange={setViewCatalog}
            />
          ) : (
            <div className="builder-empty" style={{ padding: 48, margin: "auto" }}>
              Ce module n'a pas encore d'étape.{" "}
              <button
                className="builder-btn-secondary"
                disabled={!page.content?.territory_id}
                onClick={() => setShowStepTypePicker(true)}
              >
                + Ajouter la première étape
              </button>
              {!page.content?.territory_id && (
                <p className="step-editor-section-hint" style={{ marginTop: 10 }}>
                  Choisissez d'abord un territoire dans l'onglet Module.
                </p>
              )}
            </div>
          ))}
          {mainView === "scores" && (
            <ScoreMatrix
              page={page}
              ctx={ctx}
              decisionsById={decisionsById}
              onChangeDecision={changeDecision}
              onEditDecision={openDecisionEditor}
              fullscreen={showFullscreen}
              onToggleFullscreen={() => setTableFullscreen((v) => !v)}
            />
          )}
          {mainView === "consequences" && (
            <ConsequenceMap
              page={page}
              ctx={ctx}
              decisionsById={decisionsById}
              configId={config?._id}
              configName={config?.name || config?._id}
              onPatch={patch}
              onChangeDecision={changeDecision}
              onEditDecision={openDecisionEditor}
              onEditPage={openPageOverlay}
              onCreatePage={createPageOverlay}
              fullscreen={showFullscreen}
              onToggleFullscreen={() => setTableFullscreen((v) => !v)}
            />
          )}
          {mainView === "canvas" && (
            <PageCanvas
              key={canvasKey}
              pageData={playedPage}
              gameStatus={gameStatus}
              editMode={showChrome}
              on={{
                editDecision: openDecisionEditor,
                addDecision: () => { setTab("content"); setDecisionPickerOpen(true); },
                editScores: () => { setTab("scores"); setScoresView("display"); },
                editGraphs: () => setTab("content"),
                editContent: () => setTab("content"),
              }}
            />
          )}
        </div>
      </div>

      {/* LE BANDEAU DU BAS. Le déroulé de l'atelier — absent quand aucun atelier
          n'est en contexte, une pile étant la propriété d'un atelier et non
          d'une page — et, s'il s'agit d'un module, celui de ses étapes emboîté
          dessous. Une seule des deux lignes s'ouvre à la fois. */}
      <ComposerStoryboard
        pile={pile}
        pagesById={pagesById}
        activePageId={page._id}
        configName={config?.name || config?._id}
        onSelectPage={goToPage}
        steps={isModule ? steps : null}
        activeStepIndex={activeStepIndex}
        onSelectStep={openStep}
        onAddStep={() => setShowStepTypePicker(true)}
        onRemoveStep={removeStep}
        open={storyboardOpenHere}
        onOpenChange={setStoryboardOpen}
      />

      {pendingPageId && (
        <div className="builder-picker-overlay" onClick={() => setPendingPageId(null)}>
          <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
            <div className="builder-picker-header">
              <h3>Modifications non enregistrées</h3>
              <button onClick={() => setPendingPageId(null)}>✕</button>
            </div>
            <div style={{ padding: "12px 20px" }}>
              <p style={{ margin: 0 }}>
                La page « {page._id} » a été modifiée. Que faut-il en faire avant
                d'ouvrir « {pagesById[pendingPageId]?.title || pendingPageId} » ?
              </p>
            </div>
            <div className="builder-picker-footer">
              <button className="builder-btn-danger" onClick={() => openPage(pendingPageId)}>
                Abandonner les modifications
              </button>
              <button
                className="builder-btn-primary"
                disabled={saving}
                onClick={async () => {
                  const target = pendingPageId;
                  if (await handleSave()) openPage(target);
                }}
              >
                {saving ? "Enregistrement…" : "Enregistrer et continuer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStepTypePicker && (
        <StepTypePicker onPick={createStep} onCancel={() => setShowStepTypePicker(false)} />
      )}

      {drawerDecisionId !== null && (
        <SideDrawer
          stack={[{ label: drawerDecisionId === "new" ? "Nouvelle décision" : drawerDecisionId }]}
          onClose={closeDecisionDrawer}
          onPop={closeDecisionDrawer}
        >
          <DecisionEditor
            embeddedId={drawerDecisionId}
            onClose={closeDecisionDrawer}
            parentConfig={config || { score_dic: {} }}
          />
        </SideDrawer>
      )}

      {/* LE COMPOSEUR DE LA PAGE CONSÉQUENCE, PAR-DESSUS. Même mécanisme que le
          graphe d'atelier qui pose un composeur sur lui-même : celui-ci reste
          monté, brouillon compris. L'enveloppe le place au-dessus des fenêtres
          de ce composeur (réglages d'une règle, aperçu), qui restent ouvertes
          dessous et récupèrent la page créée. */}
      {overlayPage && (
        <div className="page-composer-overlay">
          <PageComposer
            pageId={overlayPage.id}
            newType="context"
            config={config}
            closeLabel={`Retour à « ${page.title || page._id} »`}
            onClose={closePageOverlay}
            onSaved={onOverlaySaved}
            onNavigate={(id) => {
              // Partir de la page neuve sans l'avoir écrite : personne ne
              // l'attend plus, sinon la page suivante enregistrée prendrait sa place.
              overlayCreatedRef.current = null;
              setOverlayPage({ id });
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Le panneau de l'onglet ouvert. Séparé pour que la coque reste lisible. */
function Inspector({
  tab, inspectorProps, config, owners, onChangeType, onDeletePage,
  scoresView, onScoresViewChange, decisionsById,
  decisionPickerOpen, onDecisionPickerOpenChange,
  onEditDecision, onCreateDecision, onDuplicateDecision, poolPreview, onReroll,
  onOpenStep, onAddStep, onRemoveStep, onReorderSteps,
  stepSection, step, stepIndex, steps, territory, themes, viewCatalog,
  placingPoint, onPlacingPointChange, onChangeStep,
}) {
  const { page } = inspectorProps;

  // Sans atelier, aucun score n'existe : on le dit une fois, en haut du panneau
  // concerné, plutôt que d'afficher des listes vides que rien n'explique.
  const noContext = !config && (
    <div className="builder-warning-inline">
      {owners.length === 0
        ? "Cette page n'est encore dans la pile d'aucun atelier : les scores qu'elle pourrait afficher ne sont donc pas connus. Saisissez leurs identifiants à la main, ou ajoutez d'abord la page à un atelier."
        : "Choisissez un atelier de composition dans la barre du haut pour retrouver la liste de ses scores."}
    </div>
  );

  // LES SECTIONS DE L'ÉTAPE OUVERTE. Elles précèdent l'aiguillage par onglet de
  // page : ce sont des onglets d'un autre objet, et les mélanger au `switch`
  // ci-dessous ferait croire qu'ils règlent la page.
  if (stepSection && step) {
    return (
      <StepInspector
        section={stepSection}
        step={step}
        stepIndex={stepIndex}
        steps={steps}
        territoryId={page.content?.territory_id}
        availableLayers={page.content?.layers || []}
        layerCatalog={territory?.layer_catalog || []}
        themes={themes}
        catalog={viewCatalog}
        token={inspectorProps.ctx.token}
        placingPoint={placingPoint}
        onPlacingPointChange={onPlacingPointChange}
        onChangeStep={onChangeStep}
      />
    );
  }

  switch (tab) {
    case "identity":
      return <IdentityInspector {...inspectorProps} onChangeType={onChangeType} onDelete={onDeletePage} />;

    case "module":
      return (
        <>
          <h2 className="step-editor-section-title">Le module</h2>
          <p className="step-editor-section-hint">
            Le décor commun à toutes les étapes — territoire et couches chargées —
            et l'ordre du cheminement. Ce qui se règle une fois, par opposition aux
            sections de gauche qui se règlent étape par étape.
          </p>
          <TerritoryInspector
            {...inspectorProps}
            onOpenStep={onOpenStep}
            onAddStep={onAddStep}
            onRemoveStep={onRemoveStep}
            onReorderSteps={onReorderSteps}
          />
        </>
      );

    case "background":
      return (
        <>
          <h2 className="step-editor-section-title">Fond de la page</h2>
          <p className="step-editor-section-hint">
            Le décor derrière le contenu : une image, ou la carte 3D du territoire de l'atelier.
          </p>
          <BackgroundInspector {...inspectorProps} />
        </>
      );

    case "scores":
      return (
        <>
          {/* DEUX QUESTIONS DISTINCTES SOUS LE MÊME MOT. « Quels scores la page
              montre-t-elle » se règle en regardant les cartes ; « ces impacts
              sont-ils équilibrés » demande de les voir tous à la fois. Deux
              sous-onglets plutôt qu'un panneau qui répondrait mal aux deux. */}
          <div className="inspector-subtabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={scoresView === "display"}
              className={`inspector-subtab${scoresView === "display" ? " is-active" : ""}`}
              onClick={() => onScoresViewChange("display")}
            >Affichage</button>
            <button
              type="button"
              role="tab"
              aria-selected={scoresView === "synthesis"}
              className={`inspector-subtab${scoresView === "synthesis" ? " is-active" : ""}`}
              onClick={() => onScoresViewChange("synthesis")}
            >Synthèse des impacts</button>
          </div>

          {scoresView === "display" ? (
            <>
              <h2 className="step-editor-section-title">Ce que les cartes affichent</h2>
              <p className="step-editor-section-hint">
                Les scores portés par chaque carte de mesure, et le seuil qui peut
                bloquer la validation de la page.
              </p>
              {noContext}
              <ScoresInspector {...inspectorProps} />
            </>
          ) : (
            <ScoreSynthesisPanel page={page} ctx={inspectorProps.ctx} decisionsById={decisionsById} noContext={noContext} />
          )}
        </>
      );

    case "content":
    default:
      return (
        <>
          <h2 className="step-editor-section-title">Contenu</h2>
          {page.type === "context" && (
            <>
              <p className="step-editor-section-hint">Ce que la page montre : un média, du texte, un son.</p>
              <ContextContentInspector {...inspectorProps} />
            </>
          )}
          {page.type === "score" && (
            <>
              <p className="step-editor-section-hint">Le tableau de bord affiché, et les graphiques qui s'y ajoutent.</p>
              {noContext}
              <GraphsInspector {...inspectorProps} />
            </>
          )}
          {page.type === "two_doughnuts_graphs" && (
            <>
              <p className="step-editor-section-hint">Les deux scores comparés côte à côte en anneau.</p>
              {noContext}
              <DoughnutScoresInspector {...inspectorProps} />
            </>
          )}
          {page.type === "custom" && (
            <>
              <p className="step-editor-section-hint">
                L'écran React monté par cette page, qui lit l'état de la partie
                directement, et les sections de graphes à lui ajouter en bas.
              </p>
              <CustomContentInspector {...inspectorProps} />
            </>
          )}
          {hasDecisionList(page.type) && (
            <>
              <p className="step-editor-section-hint">
                Les mesures proposées : une liste que vous fixez, ou un tirage dans
                le réservoir de l'atelier.
              </p>
              {noContext}
              <DecisionsInspector
                {...inspectorProps}
                pickerOpen={decisionPickerOpen}
                onPickerOpenChange={onDecisionPickerOpenChange}
                onEditDecision={onEditDecision}
                onCreateDecision={onCreateDecision}
                onDuplicateDecision={onDuplicateDecision}
                preview={poolPreview}
                onReroll={onReroll}
              />
            </>
          )}
        </>
      );
  }
}

/**
 * Le panneau de gauche quand la synthèse des impacts occupe le centre : la
 * légende de lecture, et ce que le tableau ne peut pas dire de lui-même — ce
 * qui manque. Une case vide se remarque ; une mesure entièrement vide, ou un
 * score affiché que personne ne touche, se remarquent beaucoup moins.
 */
function ScoreSynthesisPanel({ page, ctx, decisionsById, noContext }) {
  const decisions = authoredDecisionIds(page, ctx.config).map((id) => decisionsById[id]).filter(Boolean);
  const withoutImpact = decisions.filter((d) => Object.keys(d.score || {}).length === 0);

  const touched = new Set();
  decisions.forEach((d) => Object.keys(d.score || {}).forEach((k) => touched.add(k)));
  const shownButUntouched = (page.content?.display_score_decisions || [])
    .map((d) => d.score_id)
    .filter((id) => id && !touched.has(id));

  return (
    <>
      <h2 className="step-editor-section-title">Synthèse des impacts</h2>
      <p className="step-editor-section-hint">
        Les mesures en colonnes, les scores en lignes. Cliquez une case pour
        corriger un impact — c'est la mesure qui est modifiée, pas la page.
      </p>
      {noContext}

      <div className="score-legend">
        <span className="score-legend-item"><span className="score-legend-swatch is-up" /> Gagne</span>
        <span className="score-legend-item"><span className="score-legend-swatch is-down" /> Coûte</span>
        <span className="score-legend-item"><span className="score-legend-swatch is-none" /> Aucun impact</span>
      </div>
      <p className="step-editor-section-hint">
        L'intensité compare les mesures <strong>au sein d'une même ligne</strong> :
        un coût en euros et des points de satisfaction n'ont pas la même échelle.
      </p>

      {withoutImpact.length > 0 && (
        <div className="builder-warning-inline">
          ⚠ {withoutImpact.length} mesure(s) sans aucun impact :
          {" "}{withoutImpact.map((d) => d.title || d._id).join(", ")}. En jeu, les
          choisir ne change rien.
        </div>
      )}
      {shownButUntouched.length > 0 && (
        <div className="builder-warning-inline">
          ⚠ Score(s) affiché(s) sur les cartes qu'aucune mesure ne touche :
          {" "}{shownButUntouched.join(", ")}. Les cartes montreront 0.
        </div>
      )}
      {withoutImpact.length === 0 && shownButUntouched.length === 0 && decisions.length > 0 && (
        <div className="score-legend-ok">Chaque mesure a au moins un impact, et chaque score affiché est alimenté.</div>
      )}
    </>
  );
}

